import type { NumberLookup } from './types';

export function formatTalk(seconds: number) {
  if (!seconds) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60 ? `${seconds % 60}s` : ''}`.trim();
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function formatWhen(ms?: number) {
  if (!ms) return '—';
  const date = new Date(ms);
  const now = new Date();
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (ms >= startOfToday) return `Today ${time}`;
  if (ms >= startOfToday - 86_400_000) return `Yesterday ${time}`;
  if (now.getTime() - ms < 6 * 86_400_000) return `${date.toLocaleDateString([], { weekday: 'short' })} ${time}`;
  return date.toLocaleDateString([], { day: 'numeric', month: 'short', year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric' });
}

/** "in 12 min", "3 h overdue". */
export function formatDue(dueAtMs?: number, nowMs = Date.now()) {
  if (!dueAtMs) return '';
  const minutes = Math.round((dueAtMs - nowMs) / 60_000);
  const span = (value: number) => (Math.abs(value) < 60 ? `${Math.abs(value)} min` : Math.abs(value) < 48 * 60 ? `${Math.round(Math.abs(value) / 60)} h` : `${Math.round(Math.abs(value) / 1440)} days`);
  return minutes >= 0 ? `due in ${span(minutes)}` : `${span(minutes)} overdue`;
}

export function formatMinutes(minutes: number | null | undefined) {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 6) / 10} h`;
  return `${Math.round(minutes / 144) / 10} days`;
}

export function percent(value: number | null | undefined) {
  return value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`;
}

export function initials(name: string) {
  const parts = name.replace(/[^\p{L}\p{N} ]/gu, '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '#';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function errorMessage(error: unknown, fallback = 'Something went wrong') {
  return error instanceof Error ? error.message : typeof error === 'string' ? error : fallback;
}

export const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm';

/** The same input look, sized to its content: for filter rows. */
export const inlineInputClass = inputClass.replace('w-full ', '');

export const buttonClass = {
  primary:
    'inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg text-sm font-medium transition-colors',
  secondary:
    'inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium transition-colors disabled:opacity-50',
  danger:
    'inline-flex items-center justify-center gap-2 px-4 py-2 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm font-medium transition-colors disabled:opacity-50',
  small:
    'inline-flex items-center gap-1.5 px-2.5 py-1 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md text-xs font-medium transition-colors disabled:opacity-50',
};

/** Where a look-up suggestion came from, as a short label: "Google Maps" or the web page's domain. */
export function lookupSourceLabel(lookup: NumberLookup) {
  if (lookup.source === 'google') return 'Google Maps';
  try {
    return new URL(lookup.sourceUrl ?? '').hostname.replace(/^www\./, '');
  } catch {
    return 'the web';
  }
}

/** Name and company to save a look-up suggestion under: businesses are their own company. Truecaller's name when nothing else was found. */
export function suggestionFields(lookup: NumberLookup | null | undefined, callerIdName?: string) {
  if (lookup?.status === 'found' && lookup.name) return { name: lookup.name, company: lookup.kind === 'business' ? lookup.name : undefined };
  const truecaller = truecallerResult(lookup);
  if (truecaller?.status === 'found' && truecaller.name) return { name: truecaller.name, company: truecaller.company };
  if (callerIdName) return { name: callerIdName, company: undefined };
  return null;
}

/** A search the server lost (it restarted mid-search) shows as interrupted, so the button works again. Matches STALE_PENDING_MS in convex/crmLookup.ts. */
export function truecallerResult(lookup: NumberLookup | null | undefined, nowMs = Date.now()): NonNullable<NumberLookup['truecaller']> | null {
  const result = lookup?.truecaller;
  if (result?.status === 'pending' && (lookup?.truecallerAtMs ?? 0) < nowMs - 3 * 60_000) {
    return { status: 'error', error: 'Interrupted; press Truecaller to try again' };
  }
  return result ?? null;
}

/** Numbers Truecaller can be searched for here: South African ones (0821234567, +27821234567, 0027821234567). */
export function isSouthAfricanNumber(number: string) {
  const digits = number.replace(/\D/g, '').replace(/^00/, '').replace(/^27(?=\d{9}$)/, '0');
  return /^0\d{9}$/.test(digits);
}

/** The number's page on truecaller.com, for South African numbers; staff read the name there themselves. */
export function truecallerSearchUrl(number: string) {
  if (!isSouthAfricanNumber(number)) return null;
  const national = number.replace(/\D/g, '').replace(/^00/, '').replace(/^27(?=\d{9}$)/, '0');
  return `https://www.truecaller.com/search/za/${national}`;
}

/** South African cellphone numbers (06x, 07x, 081–084); everything else is saved as a business line. */
export function phoneTypeFor(number: string) {
  const digits = number.replace(/\D/g, '').replace(/^27/, '0');
  return /^0(6|7|8[1-4])\d{7,8}$/.test(digits) ? 'mobile' : 'business';
}
